import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useOwnUserNode, useUserNodes } from '/@/hooks/nodes';
import { useHasWriteAccess, useIsValidatedBayerUserType } from '/@/hooks/session';
import { _ } from '/@/locale';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { reverse } from '/@/utils/router';
import GrowerSelectorModal from '../grower-selector-modal';
import InvitationResultsModal, { type InvitationResultItem } from './components/invitation-results-modal';
import InviteGrowerToFieldviewStep from './components/invite-grower-to-fieldview-step';
import LinkWithFieldviewStep from './components/link-with-fieldview-step';
import Step1 from './components/step1';

const NewFieldGrowersModal = ({
    open,
    setOpen,
    rvUserNodeId,
    growerUserNodeId: growerUserNodeIdProp,
    trackEntrySource,
    returnUrl,
}: {
    open: boolean;
    setOpen: (open: boolean) => void;
    rvUserNodeId?: number;
    growerUserNodeId?: number;
    trackEntrySource?: Parameters<typeof mpTrackEvent<'Field create began'>>['1']['entry_source'];
    /** Custom URL to return to after field creation. If not provided, uses current location. */
    returnUrl?: string;
}) => {
    const navigate = useNavigate();
    const isBayerUserType = useIsValidatedBayerUserType();
    const hasWriteAccess = useHasWriteAccess();

    const [step, setStep] = useState<number>(1);

    const userNodesQuery = useUserNodes({ page_size: 10000, page: 1, node: rvUserNodeId });
    const ownUserQuery = useOwnUserNode();
    const filteredUserNodes = useMemo(() => {
        return userNodesQuery.data?.results
            .map((userNode) => ({
                ...userNode,
                name: userNode.isSelf ? _('Mi cuenta') : userNode.name,
            }))
            .filter(
                (userNode) =>
                    (userNode.user === null || !userNode.user?.authProviders?.includes('fieldview')) &&
                    (!growerUserNodeIdProp || userNode.id === growerUserNodeIdProp) &&
                    userNode.id !== ownUserQuery.data?.id
            );
    }, [userNodesQuery.data, growerUserNodeIdProp, ownUserQuery.data?.id]);

    const userNodesConnectedWithFieldview = useMemo(() => {
        return (
            userNodesQuery.data?.results
                ?.map((userNode) => ({
                    ...userNode,
                    name: userNode.isSelf ? _('Mi cuenta') : userNode.name,
                }))
                .filter(
                    (userNode) =>
                        userNode.user?.authProviders?.includes('fieldview') &&
                        userNode.id !== ownUserQuery.data?.id &&
                        (!growerUserNodeIdProp || userNode.id === growerUserNodeIdProp)
                ) ?? []
        );
    }, [userNodesQuery.data, growerUserNodeIdProp, ownUserQuery.data?.id]);

    // Step 1 choices
    const [selectedOption, setSelectedOption] = useState<'draw' | 'upload' | 'import' | null>(null);
    const createOwnUserField = !(isBayerUserType && hasWriteAccess) || ownUserQuery.data?.id === growerUserNodeIdProp;

    // Filters for Step 2
    const [selectedUserNodeId, setSelectedUserNodeId] = useState<number | null>(growerUserNodeIdProp ?? null);
    const [selectedUserNodeIds, setSelectedUserNodeIds] = useState<Set<number>>(new Set());
    const [search, setSearch] = useState('');

    // Step 3 confirmation
    const [isInvitationSent, setIsInvitationSent] = useState(false);
    const [invitationResults, setInvitationResults] = useState<{
        results: InvitationResultItem[];
        showSegmentoColumn: boolean;
        advisorNameByNodeId: Record<number, string>;
    } | null>(null);

    const resetState = () => {
        setSelectedUserNodeIds(new Set());
        setSelectedOption(null);
        setIsInvitationSent(false);
        setInvitationResults(null);
        setStep(1);
        setOpen(false);
    };

    const handleBack = () => {
        if (step === 2) {
            return setStep(1);
        }
        return resetState();
    };

    const handleInvitationSent = (
        results: InvitationResultItem[],
        meta: { showSegmentoColumn: boolean; advisorNameByNodeId: Record<number, string> }
    ) => {
        setIsInvitationSent(true);
        setInvitationResults({
            results,
            showSegmentoColumn: meta.showSegmentoColumn,
            advisorNameByNodeId: meta.advisorNameByNodeId,
        });
    };

    useEffect(() => {
        if (open) {
            setSelectedUserNodeId(growerUserNodeIdProp ?? null);
            mpTrackEvent('Field Modal Opened', {
                entry_source: trackEntrySource,
            });
        }
    }, [open, trackEntrySource, growerUserNodeIdProp]);

    const bodyProps = {
        sx: {
            overflow: 'auto',
            maxHeight: { xs: '80vh' },
        },
        spacing: 3,
    };
    const actionsProps = {
        sx: {
            justifyContent: 'end',
        },
    };

    if (!open) return null;

    return (
        <>
            {step === 1 && (
                <Step1
                    selectedOption={selectedOption}
                    setSelectedOption={(option) => setSelectedOption(option as 'draw' | 'upload' | 'import' | null)}
                    userNodesFilteredByFieldview={filteredUserNodes || []}
                    createOwnUserField={createOwnUserField}
                    trackEntrySource={trackEntrySource}
                    setStep={setStep}
                    selectedUserNodeId={selectedUserNodeId}
                    bodyProps={{ ...bodyProps, alignItems: 'flex-start', textAlign: 'left' }}
                    actionsProps={actionsProps}
                    onClose={resetState}
                    open={open}
                    returnUrl={returnUrl}
                />
            )}
            {step === 2 && rvUserNodeId && selectedOption && ['draw', 'upload'].includes(selectedOption) && (
                <GrowerSelectorModal
                    ModalV2Props={{
                        actionsProps,
                        bodyProps: { ...bodyProps, alignItems: 'flex-start', textAlign: 'left' },
                        onClose: resetState,
                        open: open,
                        title: _('Asigná el lote a un productor'),
                    }}
                    userNodeId={rvUserNodeId}
                    selectedUserNodeId={selectedUserNodeId}
                    setSelectedUserNodeId={setSelectedUserNodeId}
                    onBack={handleBack}
                    text={_('Seleccioná un productor para asociar el lote que estás por agregar.')}
                    nextAction={{
                        onClick: (): void => {
                            if (selectedUserNodeId && selectedOption && ['draw', 'upload'].includes(selectedOption)) {
                                if (trackEntrySource) {
                                    mpTrackEvent('Field create began', {
                                        entry_source: trackEntrySource,
                                        create_own_user_field: createOwnUserField,
                                    });
                                }
                                // Capture current URL as next parameter for redirect after field creation
                                const nextUrl = encodeURIComponent(location.pathname + location.search);
                                navigate(
                                    reverse('fields.NewField', {
                                        qs: { userNodeId: selectedUserNodeId, option: selectedOption, next: nextUrl },
                                    })
                                );
                            }
                        },
                        text: _('Continuar'),
                    }}
                />
            )}
            {step === 2 &&
                !!filteredUserNodes?.length &&
                selectedOption === 'import' &&
                !createOwnUserField &&
                !invitationResults && (
                    <InviteGrowerToFieldviewStep
                        userNodesFilteredByFieldview={filteredUserNodes}
                        userNodesConnectedWithFieldview={userNodesConnectedWithFieldview}
                        search={search}
                        setSearch={setSearch}
                        selectedUserNodeIds={selectedUserNodeIds}
                        setSelectedUserNodeIds={setSelectedUserNodeIds}
                        userNodesQuery={userNodesQuery}
                        trackEntrySource={trackEntrySource}
                        createOwnUserField={createOwnUserField}
                        onBack={handleBack}
                        onInvitationSent={handleInvitationSent}
                        bodyProps={{ ...bodyProps, alignItems: 'flex-start', textAlign: 'left' }}
                        actionsProps={actionsProps}
                        onClose={resetState}
                        open={open}
                    />
                )}
            {step === 2 && selectedOption === 'import' && createOwnUserField && (
                <LinkWithFieldviewStep
                    onClose={resetState}
                    bodyProps={bodyProps}
                    actionsProps={actionsProps}
                    open={open}
                />
            )}
            {invitationResults && (
                <InvitationResultsModal
                    open={isInvitationSent}
                    onClose={resetState}
                    results={invitationResults.results}
                    showSegmentoColumn={invitationResults.showSegmentoColumn}
                    advisorNameByNodeId={invitationResults.advisorNameByNodeId}
                />
            )}
        </>
    );
};

export default NewFieldGrowersModal;
